import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { ShellQuery } from '@shared/store/shell/shell-query';

import { IntuitiveIndication, getJawByToothNumber, unitTypeFolderNames, unitTypeInBridgeFolderNames } from '../models';
import { INTUITIVE_INDICATION_BRIDGE_OFFSET } from '../models/intuitive-indication';
// eslint-disable-next-line @typescript-eslint/naming-convention
import DOMPurify from 'dompurify';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

const forceEscapeCharsRegexp = (s: string): string => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const JAW_FOLDERS_FILENAMES: Record<string, (jaw: string) => string> = {
	// eslint-disable-next-line @typescript-eslint/naming-convention
	'scan-body': (jaw: string) => `tooth_scan_body_${jaw}`,
	// eslint-disable-next-line @typescript-eslint/naming-convention
	'implant-abutment': (jaw: string) => `tooth_implant_abutment_${jaw}`
};

@Injectable({ providedIn: 'root' })
export class RxIntuitiveTeethImagesService {
	private readonly spriteCache = new Map<string, SafeHtml>();

	constructor(
		private readonly shellQuery: ShellQuery,
		private readonly http: HttpClient,
		private readonly domSanitizer: DomSanitizer
	) {}

	getSpriteFolder(indicationId: IntuitiveIndication | null): string {
		if (indicationId == null) {
			return 'regular';
		}

		if ((indicationId as number) >= INTUITIVE_INDICATION_BRIDGE_OFFSET) {
			return unitTypeInBridgeFolderNames[indicationId - INTUITIVE_INDICATION_BRIDGE_OFFSET] ?? 'regular';
		}

		return unitTypeFolderNames[indicationId] ?? 'regular';
	}

	getSpriteUrl(indicationId: IntuitiveIndication | null): string {
		const folder = this.getSpriteFolder(indicationId);

		return `${this.shellQuery.staticFilesEndpoint}/assets/intuitive-teeth/${folder}.svg`;
	}

	/**
	 * Fetches sprite SVG (cached). Markup is returned with symbol ids/refs prefixed by folder
	 * so multiple sprites can be combined in one document.
	 */
	getSpriteContent(indicationId: IntuitiveIndication | null): Observable<SafeHtml> {
		const folder = this.getSpriteFolder(indicationId);
		const cached = this.spriteCache.get(folder);

		if (cached != null) {
			return of(cached);
		}

		const url = this.getSpriteUrl(indicationId);

		return this.http.get(url, { responseType: 'text' }).pipe(
			map(markup => this.prefixSpriteIds(markup, folder)),
			map(prefixed => this.domSanitizer.bypassSecurityTrustHtml(DOMPurify.sanitize(prefixed))),
			tap(safeHtml => this.spriteCache.set(folder, safeHtml))
		);
	}

	/**
	 * Prefix root-level symbol ids and their internal refs with folder name so multiple sprites
	 * in the same document do not clash (e.g. tooth_1 -> crown_tooth_1).
	 */
	prefixSpriteIds(markup: string, folder: string): string {
		const symbolIdRegex = /<symbol\s+id="([^"]+)"/g;
		const ids: string[] = [];
		let match: RegExpExecArray | null;

		while ((match = symbolIdRegex.exec(markup)) !== null) {
			ids.push(match[1]);
		}

		const sortedIds = [...new Set(ids)].sort((a, b) => b.length - a.length);

		let result = markup;

		for (const sid of sortedIds) {
			const prefixed = `${folder}_${sid}`;

			result = result.replace(new RegExp(`id="${forceEscapeCharsRegexp(sid)}"`, 'g'), `id="${prefixed}"`);
			result = result.replace(new RegExp(`id="${forceEscapeCharsRegexp(sid)}_`, 'g'), `id="${prefixed}_`);
			result = result.replace(new RegExp(`(href|xlink:href)="#${forceEscapeCharsRegexp(sid)}"`, 'g'), `$1="#${prefixed}"`);
			result = result.replace(new RegExp(`(href|xlink:href)="#${forceEscapeCharsRegexp(sid)}_`, 'g'), `$1="#${prefixed}_`);
			result = result.replace(new RegExp(`url\\(#${forceEscapeCharsRegexp(sid)}\\)`, 'g'), `url(#${prefixed})`);
			result = result.replace(new RegExp(`url\\(#${forceEscapeCharsRegexp(sid)}_`, 'g'), `url(#${prefixed}_`);
		}

		return result;
	}

	getSymbolId(toothId: number, indicationId: IntuitiveIndication | null): string {
		const folder = this.getSpriteFolder(indicationId);
		let baseId: string;

		if (indicationId != null && Object.keys(JAW_FOLDERS_FILENAMES).includes(folder)) {
			const jaw = getJawByToothNumber({ toothId });

			baseId = JAW_FOLDERS_FILENAMES[folder](jaw);
		} else {
			baseId = `tooth_${toothId}`;
		}

		return `${folder}_${baseId}`;
	}
}
